// ---- Bracket logic ------------------------------------------------------
// Pure functions over plain state objects. No React, no DOM — node-testable.

import {
  R32_MATCHES,
  R16_PAIRS,
  QF_PAIRS,
  SF_PAIRS,
  F_PAIRS,
  R16_BASE,
  QF_BASE,
  SF_BASE,
  F_BASE,
  THIRD_SLOT_ELIGIBILITY,
  THIRD_SLOT_IDS
} from "./data.js";

// Given the 8 groups whose third-placed teams advanced, assign each to a distinct
// eligible R32 slot (a bipartite matching). Returns { matchId: groupLetter } or
// null if no perfect matching exists. Simple backtracking (8 items, trivial).
export function assignThirds(qualifiedGroups) {
  const slots = THIRD_SLOT_IDS;
  const result = {};
  const usedGroups = new Set();
  const solve = i => {
    if (i === slots.length) return true;
    const slot = slots[i];
    const eligible = THIRD_SLOT_ELIGIBILITY[slot];
    for (const g of qualifiedGroups) {
      if (!usedGroups.has(g) && eligible.includes(g)) {
        result[slot] = g;
        usedGroups.add(g);
        if (solve(i + 1)) return true;
        usedGroups.delete(g);
        delete result[slot];
      }
    }
    return false;
  };
  return solve(0) ? result : null;
}

// Resolve a slot descriptor to a team name (or null). For "third" slots we look
// up which group was assigned to this specific match id.
export function resolveSlot(slot, matchId, assignment, groupResults) {
  if (!slot) return null;
  if (slot.t === "winner") return groupResults[slot.g]?.first || null;
  if (slot.t === "runner") return groupResults[slot.g]?.second || null;
  if (slot.t === "third") {
    const g = assignment?.[matchId];
    return g ? groupResults[g]?.third || null : null;
  }
  return null;
}

// Build the resolved Round of 32 (teams filled in from group results + thirds).
export function buildR32(groupResults, assignment) {
  return R32_MATCHES.map(m => ({
    id: m.id,
    home: resolveSlot(m.home, m.id, assignment, groupResults),
    away: resolveSlot(m.away, m.id, assignment, groupResults),
    homeSlot: m.home,
    awaySlot: m.away
  }));
}

// Build a round from the previous round's winners and a pairing table.
export function buildRound(prevTeams, pairs, baseId) {
  return pairs.map((pair, i) => ({
    id: baseId + i,
    home: prevTeams[pair[0]] ?? null,
    away: prevTeams[pair[1]] ?? null
  }));
}

// Build every knockout round from group results, the thirds assignment, and the
// current winners map. Returns { r32, r16, qf, sf, final, champion }.
export function buildBracket(groupResults, assignment, winners) {
  const winnerOf = m => (m ? winners[m.id] ?? null : null);
  const r32 = buildR32(groupResults, assignment);
  const r16 = buildRound(r32.map(winnerOf), R16_PAIRS, R16_BASE);
  const qf = buildRound(r16.map(winnerOf), QF_PAIRS, QF_BASE);
  const sf = buildRound(qf.map(winnerOf), SF_PAIRS, SF_BASE);
  const final = buildRound(sf.map(winnerOf), F_PAIRS, F_BASE);
  return { r32, r16, qf, sf, final, champion: winnerOf(final[0]) };
}

// Remove downstream winners that no longer have a valid path after an edit.
// Rebuilds and nulls out any winner that isn't present in its match anymore,
// iterating a few times to cascade. Returns a new winners object.
export function pruneDownstream(state, groupResults, assignment) {
  let s = { ...state };
  for (let iter = 0; iter < 6; iter++) {
    const { r16, qf, sf, final } = buildBracket(groupResults, assignment, s);
    const all = [...r16, ...qf, ...sf, ...final];
    let changed = false;
    for (const m of all) {
      const w = s[m.id];
      if (w && w !== m.home && w !== m.away) {
        delete s[m.id];
        changed = true;
      }
    }
    if (!changed) break;
  }
  return s;
}
